import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, resolveImage, errorText } from "@/lib/api";
import { Field, inputClass, GhostButton } from "./fields";

export default function ImageField({ label, value, onChange, testId, aspect = "aspect-[16/9]" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(errorText(err, "Upload failed."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Field label={label} hint="Paste an image URL or upload a JPG / PNG / WEBP (max 8 MB).">
      <div className="flex gap-3">
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="https://…" data-testid={`${testId}-url`} className={inputClass} />
        <GhostButton type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testId}-upload`} className="shrink-0 inline-flex items-center gap-2">
          <Upload size={12} /> {uploading ? "Uploading…" : "Upload"}
        </GhostButton>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${testId}-file`} />
      </div>
      {value && (
        <div className={`mt-3 ${aspect} w-full max-w-sm overflow-hidden border border-navy-ink/10 bg-white`}>
          <img src={resolveImage(value)} alt="" className="h-full w-full object-cover" data-testid={`${testId}-preview`} />
        </div>
      )}
    </Field>
  );
}
